import React, { useState, useEffect } from 'react';
import SuperAdminLayout from '../../../components/hostel/superadmin/SuperAdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const ManageStudents = ({ onNavigate, userInfo }) => {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterHostel, setFilterHostel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editFormData, setEditFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    guardian_name: '',
    guardian_phone: '',
    course: '',
    year_of_study: '',
    blood_group: '',
    gender: '',
    date_of_birth: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    batch_name: '',
    admission_number: '',
    guardian_email: '',
    guardian_relation: '',
    emergency_contact_name: '',
    emergency_contact_phone: ''
  });

  useEffect(() => {
    if (userInfo?.userId) {
      fetchStudents();
      fetchHostels();
    }
  }, [userInfo]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/superadmin/students/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setStudents(data.students || []);
      } else {
        setError(data.message || 'Failed to load students');
      }
    } catch (err) {
      console.error('Error fetching students:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchHostels = async () => {
    try {
      const response = await fetch(`${API_URL}/superadmin/hostels/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setHostels(data.hostels || []);
      }
    } catch (err) {
      console.error('Error fetching hostels:', err);
    }
  };

  const handleViewDetails = (student) => {
    setSelectedStudent(student);
    setShowDetailsModal(true);
  };

  const handleEditStudent = (student) => {
    setSelectedStudent(student);
    setEditFormData({
      full_name: student.full_name || '',
      email: student.email || '',
      phone_number: student.phone_number || '',
      guardian_name: student.guardian_name || student.sa_guardian_name || '',
      guardian_phone: student.guardian_phone || student.sa_guardian_phone || '',
      guardian_email: student.guardian_email || '',
      guardian_relation: student.guardian_relation || '',
      course: student.course || '',
      year_of_study: student.year_of_study || '',
      blood_group: student.blood_group || '',
      gender: student.gender || '',
      date_of_birth: student.date_of_birth ? new Date(student.date_of_birth).toISOString().split('T')[0] : '',
      address: student.address || student.sa_address || student.permanent_address || '',
      city: student.city || '',
      state: student.state || '',
      pincode: student.pincode || '',
      batch_name: student.batch_name || '',
      admission_number: student.admission_number || '',
      emergency_contact_name: student.emergency_contact_name || '',
      emergency_contact_phone: student.emergency_contact_phone || ''
    });
    setShowEditModal(true);
  };

  /* Transfer Modal State */
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferFormData, setTransferFormData] = useState({
    newHostelId: '',
    newHostelName: ''
  });

  const handleTransferClick = (student) => {
    setSelectedStudent(student);
    setTransferFormData({
      newHostelId: '',
      newHostelName: ''
    });
    setShowTransferModal(true);
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStudent || !transferFormData.newHostelId) {
      alert('Please select a hostel');
      return;
    }

    // Try to find the application ID. If not directly on student, we might need a fallback.
    // Assuming backend returns it or we can infer it. 
    // The previous implementation inferred it or used a property.
    // Let's rely on student.application_id if available, or try to use user_id if the backend supports it (it might not).
    // The server.js route we added expects :id which is applicationId.

    const appId = selectedStudent.application_id || selectedStudent.id; // fallback to id if application_id missing

    try {
      const response = await fetch(`${API_URL}/superadmin/change-hostel/${appId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          newHostelId: transferFormData.newHostelId,
          newHostelName: transferFormData.newHostelName,
          adminId: userInfo.userId
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('Student transferred successfully');
        setShowTransferModal(false);
        fetchStudents();
      } else {
        alert(data.message || 'Failed to transfer student');
      }
    } catch (err) {
      console.error('Error transferring student:', err);
      alert('Failed to transfer student');
    }
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    if (!selectedStudent) return;

    try {
      const response = await fetch(
        `${API_URL}/superadmin/update-student/${userInfo.userId}/${selectedStudent.user_id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(editFormData)
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Student details updated successfully!');
        setShowEditModal(false);
        fetchStudents();
      } else {
        alert(data.message || 'Failed to update student details');
      }
    } catch (err) {
      console.error('Error updating student:', err);
      alert('Failed to update student details');
    }
  };

  const handleDeactivateStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to deactivate this student?')) {
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/superadmin/student/${userInfo.userId}/${studentId}`,
        {
          method: 'DELETE'
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Student deactivated successfully!');
        fetchStudents();
      } else {
        alert(data.message || 'Failed to deactivate student');
      }
    } catch (err) {
      console.error('Error deactivating student:', err);
      alert('Failed to deactivate student');
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch =
      student.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.phone_number?.includes(searchQuery) ||
      student.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.room_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.course?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesHostel = filterHostel === 'all' || student.hostel_name === filterHostel;
    const matchesStatus = filterStatus === 'all' || student.current_status === filterStatus;

    return matchesSearch && matchesHostel && matchesStatus;
  });

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'checked-in': return 'status-active';
      case 'checked-out': return 'status-inactive';
      case 'on-leave': return 'status-warning';
      case 'suspended': return 'status-danger';
      default: return 'status-default';
    }
  };

  return (
    <SuperAdminLayout
      onNavigate={onNavigate}
      userInfo={userInfo}
      activePage="hostel-superadmin-manage-students"
      pageTitle="Manage Students 👥"
    >
      <style>{`
        .controls-section {
          display: flex;
          gap: 20px;
          margin-bottom: 30px;
          flex-wrap: wrap;
          align-items: center;
        }

        .search-box {
          flex: 1;
          min-width: 300px;
          position: relative;
        }

        .search-input {
          width: 100%;
          padding: 14px 20px 14px 50px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s;
          background: white;
        }

        .search-input:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .search-icon {
          position: absolute;
          left: 18px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 20px;
          color: #52ab98;
        }

        .filter-select {
          padding: 14px 20px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          color: #2b6777;
          background: white;
          cursor: pointer;
          transition: all 0.3s;
        }

        .filter-select:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .stats-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .stat-card {
          background: white;
          padding: 24px;
          border-radius: 16px;
          border: 2px solid rgba(82,171,152,0.15);
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .stat-label {
          font-size: 13px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 900;
          color: #2b6777;
        }

        .students-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
          gap: 24px;
        }

        .student-card {
          background: white;
          border-radius: 20px;
          padding: 28px;
          border: 2px solid rgba(82,171,152,0.15);
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }

        .student-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 6px;
          height: 100%;
          background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
        }

        .student-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(43,103,119,0.15);
          border-color: rgba(82,171,152,0.3);
        }

        .student-header {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 20px;
        }

        .student-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          color: white;
          font-weight: 800;
          flex-shrink: 0;
        }

        .student-info {
          flex: 1;
        }

        .student-name {
          font-size: 20px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 6px;
        }

        .student-course {
          font-size: 13px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
        }

        .student-details {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 20px;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .detail-label {
          font-size: 12px;
          color: rgba(43,103,119,0.6);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-value {
          font-size: 14px;
          font-weight: 700;
          color: #2b6777;
          word-break: break-word;
        }

        .student-actions {
          display: flex;
          gap: 10px;
          padding-top: 20px;
          border-top: 2px solid rgba(82,171,152,0.1);
        }

        .action-btn {
          flex: 1;
          padding: 12px;
          border: none;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .action-btn-view {
          background: rgba(82,171,152,0.15);
          color: #2b6777;
        }

        .action-btn-view:hover {
          background: rgba(82,171,152,0.25);
          transform: translateY(-2px);
        }

        .action-btn-edit {
          background: rgba(43,103,119,0.15);
          color: #2b6777;
        }

        .action-btn-edit:hover {
          background: rgba(43,103,119,0.25);
          transform: translateY(-2px);
        }

        .action-btn-delete {
          background: rgba(239,68,68,0.15);
          color: #dc2626;
        }

        .action-btn-delete:hover {
          background: rgba(239,68,68,0.25);
          transform: translateY(-2px);
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
        }

        .status-active {
          background: rgba(34,197,94,0.15);
          color: #16a34a;
        }

        .status-inactive {
          background: rgba(107,114,128,0.15);
          color: #4b5563;
        }

        .status-warning {
          background: rgba(251,146,60,0.15);
          color: #ea580c;
        }

        .status-danger {
          background: rgba(239,68,68,0.15);
          color: #dc2626;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(43,103,119,0.6);
          backdrop-filter: blur(8px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 24px;
          width: 100%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 24px 70px rgba(43,103,119,0.3);
        }

        .modal-header {
          padding: 28px 32px;
          border-bottom: 2px solid rgba(82,171,152,0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .modal-title {
          font-size: 24px;
          font-weight: 900;
          color: #2b6777;
        }

        .modal-close {
          background: rgba(82,171,152,0.15);
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          cursor: pointer;
          font-size: 20px;
          color: #2b6777;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .modal-close:hover {
          background: rgba(82,171,152,0.25);
          transform: rotate(90deg);
        }

        .modal-body {
          padding: 32px;
        }

        .modal-footer {
            padding: 20px 32px;
            border-top: 1px solid rgba(82,171,152,0.1);
            display: flex;
            justify-content: flex-end;
            gap: 12px;
        }

        .form-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
        }

        .form-group {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }
        
        .form-group.full {
            grid-column: 1 / -1;
        }

        .form-label {
            font-size: 13px;
            font-weight: 700;
            color: #2b6777;
            text-transform: uppercase;
        }

        .form-input {
            padding: 12px;
            border: 2px solid rgba(82,171,152,0.2);
            border-radius: 10px;
            font-size: 15px;
            transition: all 0.3s;
        }

        .form-input:focus {
            outline: none;
            border-color: #52ab98;
        }

        .btn-cancel {
            padding: 12px 24px;
            border: none;
            border-radius: 10px;
            background: #f1f5f9;
            color: #64748b;
            font-weight: 700;
            cursor: pointer;
        }

        .btn-save {
            padding: 12px 24px;
            border: none;
            border-radius: 10px;
            background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
            color: white;
            font-weight: 700;
            cursor: pointer;
        }

        .detail-row {
          display: grid;
          grid-template-columns: 150px 1fr;
          gap: 16px;
          padding: 16px 0;
          border-bottom: 1px solid rgba(82,171,152,0.1);
        }

        .detail-row:last-child {
          border-bottom: none;
        }

        .detail-row-label {
          font-size: 13px;
          font-weight: 700;
          color: rgba(43,103,119,0.7);
          text-transform: uppercase;
        }

        .detail-row-value {
          font-size: 15px;
          font-weight: 600;
          color: #2b6777;
        }

        .empty-state {
          text-align: center;
          padding: 80px 20px;
          color: #666;
        }

        .empty-state-icon {
          font-size: 80px;
          margin-bottom: 20px;
        }

        .empty-state-text {
          font-size: 18px;
          color: #2b6777;
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .students-grid {
            grid-template-columns: 1fr;
          }

          .controls-section {
            flex-direction: column;
          }

          .search-box {
            width: 100%;
          }

          .stats-row {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .form-grid {
              grid-template-columns: 1fr;
          }
        }
        
        .modal-body {
            padding: 32px;
            max-height: 70vh; /* Scrollable body */
            overflow-y: auto;
        }
        
        .action-btn-transfer {
           background: rgba(245, 158, 11, 0.15);
           color: #d97706;
        }
        
        .action-btn-transfer:hover {
           background: rgba(245, 158, 11, 0.25);
           transform: translateY(-2px);
        }
      `}</style>

      {/* Stats Row */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">Total Students</div>
          <div className="stat-value">{students.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active</div>
          <div className="stat-value">
            {students.filter(s => s.current_status === 'checked-in').length}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">On Leave</div>
          <div className="stat-value">
            {students.filter(s => s.current_status === 'on-leave').length}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Filtered</div>
          <div className="stat-value">{filteredStudents.length}</div>
        </div>
      </div>

      {/* Controls */}
      <div className="controls-section">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search students by name, phone, email, room, or course..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <select
          className="filter-select"
          value={filterHostel}
          onChange={(e) => setFilterHostel(e.target.value)}
        >
          <option value="all">All Hostels</option>
          {hostels.map(hostel => (
            <option key={hostel.hostel_id} value={hostel.hostel_name}>
              {hostel.hostel_name}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="checked-in">✅ Checked In</option>
          <option value="checked-out">🚪 Checked Out</option>
          <option value="on-leave">🏖️ On Leave</option>
          <option value="suspended">🚫 Suspended</option>
        </select>
      </div>

      {/* Students Grid */}
      {loading ? (
        <div className="empty-state">
          <div className="empty-state-icon">⏳</div>
          <div className="empty-state-text">Loading students...</div>
        </div>
      ) : error ? (
        <div className="empty-state">
          <div className="empty-state-icon">❌</div>
          <div className="empty-state-text">{error}</div>
        </div>
      ) : filteredStudents.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">👥</div>
          <div className="empty-state-text">No students found</div>
        </div>
      ) : (
        <div className="students-grid">
          {filteredStudents.map((student) => (
            <div key={student.id} className="student-card">
              <div className="student-header">
                <div className="student-icon">
                  {student.full_name?.charAt(0) || '?'}
                </div>
                <div className="student-info">
                  <div className="student-name">{student.full_name}</div>
                  <div className="student-course">{student.course || 'N/A'}</div>
                </div>
              </div>

              <div className="student-details">
                <div className="detail-item">
                  <span className="detail-label">Phone</span>
                  <span className="detail-value">{student.phone_number}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{student.email}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Hostel</span>
                  <span className="detail-value">{student.hostel_name || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Room</span>
                  <span className="detail-value">{student.room_number || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Balance</span>
                  <span className="detail-value">₹{student.current_balance || 0}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className={`status-badge ${getStatusBadgeClass(student.current_status)}`}>
                    {student.current_status || 'Unknown'}
                  </span>
                </div>
              </div>

              <div className="student-actions">
                <button
                  className="action-btn action-btn-view"
                  onClick={() => handleViewDetails(student)}
                >
                  👁️ View
                </button>
                <button
                  className="action-btn action-btn-edit"
                  onClick={() => handleEditStudent(student)}
                >
                  ✏️ Edit
                </button>
                <button
                  className="action-btn action-btn-transfer"
                  onClick={() => handleTransferClick(student)}
                >
                  ⇄ Transfer
                </button>
                <button
                  className="action-btn action-btn-delete"
                  onClick={() => handleDeactivateStudent(student.user_id)}
                >
                  🗑️ Deactivate
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedStudent && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Student Details</h2>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-row">
                <div className="detail-row-label">Full Name</div>
                <div className="detail-row-value">{selectedStudent.full_name}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Phone Number</div>
                <div className="detail-row-value">{selectedStudent.phone_number}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Email</div>
                <div className="detail-row-value">{selectedStudent.email}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Hostel</div>
                <div className="detail-row-value">{selectedStudent.hostel_name || 'N/A'}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Room Number</div>
                <div className="detail-row-value">{selectedStudent.room_number || 'N/A'}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Block</div>
                <div className="detail-row-value">{selectedStudent.block_name || 'N/A'}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Floor</div>
                <div className="detail-row-value">{selectedStudent.floor_number || 'N/A'}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Course</div>
                <div className="detail-row-value">{selectedStudent.course || 'N/A'}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Year of Study</div>
                <div className="detail-row-value">{selectedStudent.year_of_study || 'N/A'}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Current Balance</div>
                <div className="detail-row-value">₹{selectedStudent.current_balance || 0}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Total Fees</div>
                <div className="detail-row-value">₹{selectedStudent.total_fees || 0}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Fees Paid</div>
                <div className="detail-row-value">₹{selectedStudent.fees_paid || 0}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Meal Plan</div>
                <div className="detail-row-value">{selectedStudent.meal_plan || 'N/A'}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Food Preference</div>
                <div className="detail-row-value">{selectedStudent.food_preference || 'N/A'}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Status</div>
                <div className="detail-row-value">
                  <span className={`status-badge ${getStatusBadgeClass(selectedStudent.current_status)}`}>
                    {selectedStudent.current_status || 'Unknown'}
                  </span>
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Joining Date</div>
                <div className="detail-row-value">
                  {selectedStudent.joining_date ? new Date(selectedStudent.joining_date).toLocaleDateString() : 'N/A'}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Guardian Name</div>
                <div className="detail-row-value">{selectedStudent.guardian_name || 'N/A'}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Guardian Phone</div>
                <div className="detail-row-value">{selectedStudent.guardian_phone || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Student Modal */}
      {showEditModal && selectedStudent && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Student Details</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                ✕
              </button>
            </div>
            <form onSubmit={handleUpdateStudent}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Full Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFormData.full_name}
                      onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Admission Number</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFormData.admission_number}
                      onChange={(e) => setEditFormData({ ...editFormData, admission_number: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={editFormData.phone_number}
                      onChange={(e) => setEditFormData({ ...editFormData, phone_number: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Course</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFormData.course}
                      onChange={(e) => setEditFormData({ ...editFormData, course: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Batch Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFormData.batch_name}
                      onChange={(e) => setEditFormData({ ...editFormData, batch_name: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Year of Study</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFormData.year_of_study}
                      onChange={(e) => setEditFormData({ ...editFormData, year_of_study: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Date of Birth</label>
                    <input
                      type="date"
                      className="form-input"
                      value={editFormData.date_of_birth}
                      onChange={(e) => setEditFormData({ ...editFormData, date_of_birth: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Gender</label>
                    <select
                      className="form-input"
                      value={editFormData.gender}
                      onChange={(e) => setEditFormData({ ...editFormData, gender: e.target.value })}
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Blood Group</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFormData.blood_group}
                      onChange={(e) => setEditFormData({ ...editFormData, blood_group: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Guardian Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFormData.guardian_name}
                      onChange={(e) => setEditFormData({ ...editFormData, guardian_name: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Guardian Phone</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={editFormData.guardian_phone}
                      onChange={(e) => setEditFormData({ ...editFormData, guardian_phone: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Guardian Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={editFormData.guardian_email}
                      onChange={(e) => setEditFormData({ ...editFormData, guardian_email: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Guardian Relation</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFormData.guardian_relation}
                      onChange={(e) => setEditFormData({ ...editFormData, guardian_relation: e.target.value })}
                    />
                  </div>

                  <div className="form-group full">
                    <label className="form-label">Permanent Address</label>
                    <textarea
                      className="form-input"
                      rows="3"
                      value={editFormData.address}
                      onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFormData.city}
                      onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFormData.state}
                      onChange={(e) => setEditFormData({ ...editFormData, state: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Pincode</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFormData.pincode}
                      onChange={(e) => setEditFormData({ ...editFormData, pincode: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="submit" className="btn-save">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && (
        <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Transfer Student</h2>
              <button className="modal-close" onClick={() => setShowTransferModal(false)}>×</button>
            </div>
            <form onSubmit={handleTransferSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Select New Hostel</label>
                  <select
                    className="form-input"
                    value={transferFormData.newHostelId}
                    onChange={(e) => {
                      const selectedHostel = hostels.find(h => h.hostel_id.toString() === e.target.value);
                      setTransferFormData({
                        newHostelId: e.target.value,
                        newHostelName: selectedHostel ? selectedHostel.hostel_name : ''
                      });
                    }}
                    required
                  >
                    <option value="">-- Choose Hostel --</option>
                    {hostels.map(hostel => (
                      <option key={hostel.hostel_id} value={hostel.hostel_id}>
                        {hostel.hostel_name}
                      </option>
                    ))}
                  </select>
                </div>
                <p style={{ marginTop: '15px', color: '#666', fontSize: '14px' }}>
                  ⚠️ Transferring this student will reset their current room assignment.
                  The admin of the new hostel will need to assign a room.
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowTransferModal(false)}>Cancel</button>
                <button type="submit" className="btn-save">Confirm Transfer</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </SuperAdminLayout>
  );
};

export default ManageStudents;